import React from 'react';
import styled from 'styled-components';

import SectionContainer from '../components/SectionContainer';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';

// About Hero
const AboutHero = styled.div`
  padding-top: 150px;
  padding-bottom: var(--spacing-lg);
`;

// About Section
const AboutSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AboutTitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const AboutText = styled.p`
  margin-bottom: var(--spacing-md);
  line-height: 1.8;
`;

const AboutImage = styled.div<{ $bgImage: string }>`
  height: 500px;
  background-image: url(${props => props.$bgImage});
  background-size: contain; /* Afficher l'image en entier */
  background-position: center;
  background-repeat: no-repeat; /* Pas de répétition pour éviter les artefacts */
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

// Team Section
const TeamContainer = styled.div`
  margin-top: var(--spacing-lg);
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
`;

const TeamMember = styled.div`
  text-align: center;
`;

const TeamMemberImage = styled.div<{ $bgImage: string }>`
  height: 300px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center top;
  margin-bottom: var(--spacing-md);
`;

const TeamMemberName = styled.h3`
  margin-bottom: var(--spacing-xs);
`;

const TeamMemberRole = styled.p`
  font-weight: 300;
  margin-bottom: var(--spacing-sm);
  opacity: 0.8;
`;

const TeamMemberBio = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
`;

// Values Section
const ValuesContainer = styled.div`
  margin-top: var(--spacing-lg);
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-lg);
`;

const ValueCard = styled.div`
  padding: var(--spacing-lg);
  border: 1px solid var(--color-medium-gray);
`;

const ValueTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
`;

const ValueDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
`;

// CTA Section
const CtaContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CtaText = styled.p`
  margin-bottom: var(--spacing-lg);
  font-size: 1.1rem;
`;

// Testimonials Section
const TestimonialsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Testimonial = styled.div`
  background-color: var(--color-background);
  padding: var(--spacing-lg);
  border-left: 4px solid var(--color-accent);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TestimonialText = styled.p`
  font-style: italic;
  margin-bottom: var(--spacing-md);
  line-height: 1.7;
  flex-grow: 1;
`;

const TestimonialAuthor = styled.p`
  font-weight: var(--font-weight-bold);
  margin: 0;
`;

const TestimonialRole = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
  margin: 0;
`;

const AboutPage: React.FC = () => {
  const testimonials = [
    {
      text: "Une tres belle experience avec Fabienne de Pirey qui a refait pour nous un chalet a Megeve: un excellent melange de modernite, de montagne, de comfort, ...une tres grande reussite dans lequel nous aimons tous nous retrouver. Je recommande tres vivement !",
      author: "Ghislaine COLELLA",
      role: "Propriétaires, Paris 16ème"
    },
    {
      text: "Accompagnement d’une professionnelle de l’architecture et décoration d’intérieur Un sens du détail et de la couleur Après 3 expériences de chantier de rénovation totale, je recommande chaleureusement Fabienne pour sa créativité, son excellent goût (qui sait évidemment s’adapter au client) et son énergie",
      author: "Catherine de la Fouchardière",
      role: "Propriétaire, Paris 9ème"
    }
  ];

  return (
    <>
      <SectionContainer background="white" spacing="small">
        <AboutHero>
          <PageHeader 
            title="À PROPOS DE NOUS" 
            subtitle="Découvrez notre histoire.." 
          />
        </AboutHero>
      </SectionContainer>
      
      <SectionContainer background="light-gray">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)', alignItems: 'start' }}>
          {/* Colonne gauche : Première image + deuxième image en dessous */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <AboutImage $bgImage="/images/about.png" />
            <div style={{ marginTop: 'var(--spacing-lg)' }}>
              <AboutImage $bgImage="/images/apropos.jpg" />
            </div>
          </div>
          
          {/* Colonne droite : Texte + troisième image en dessous */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <AboutContent>
              
              <AboutText>
                Fondée en 2005, l'Agence Intérieurs a été créée pour répondre aux exigences des particuliers
                et des entreprises en matière de rénovation, agencement intérieur et
                transformation des espaces de vie et de travail.
              </AboutText>
              <AboutText>
                Après des études au CELSA, à l'école BOULLE puis à l'école G. Ferrandi en connaissance des textiles et des couleurs, Fabienne Boé de Pirey a créé son Agence en 2010. Elle travaille en relation étroite avec des architectes DPLG si besoin et s’appuie sur un réseau d’équipes et d’artisans sélectionnés avec le plus grand soin pour leurs compétences démontrées.
              </AboutText>
              <AboutText>
                Nous sommes en recherche constante de nouveaux matériaux, de nouvelles
                finitions afin de réaliser des projets tous différents les uns des autres.
                Nous aménageons avec le même engouement des appartements, hôtels
                particuliers, maisons, résidences secondaires mais aussi bureaux, sièges sociaux
                dans leur ensemble, hôpitaux, plateaux de télévision ou lieux de
                réception…
              </AboutText>
              <AboutText>
                C'est une équipe complète et soudée qui intervient sur chaque Projet, de la
                création de celui-ci à la mise en place finale.
              </AboutText>
            </AboutContent>
            
          </div>
        </div>
      </SectionContainer>
      
      <SectionContainer background="white">
        <CtaContent>
          <CtaTitle>Envie de travailler avec nous ?</CtaTitle>
          <CtaText>
            Nous serions ravis de discuter de votre projet et de voir comment nous pouvons vous accompagner.
          </CtaText>
          <Button to="/contact" size="large">Prendre rendez-vous</Button>
        </CtaContent>
      </SectionContainer>
    </>
  );
};

export default AboutPage;
